import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Wrench, Zap } from "lucide-react";

// Demo quick-fill appears only when the server says this is a preview/development
// site (never on the published app), and never in a build marked production.
const IS_PRODUCTION = process.env.REACT_APP_ENV === "production";

const demoAccounts = [
  { role: "Owner", email: "hamarcarepharmacy010@gmail.com", pw: "admin123" },
  { role: "Manager", email: "manager@shop.com", pw: "mgr123" },
  { role: "Reception", email: "reception@shop.com", pw: "recep123" },
  { role: "Mechanic", email: "mechanic@shop.com", pw: "mech123" },
  { role: "Cashier", email: "cashier@shop.com", pw: "cash123" },
  { role: "Customer", email: "customer@shop.com", pw: "cust123" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    if (IS_PRODUCTION) return;
    api.get("/app-mode").then(r => setShowDemo(r.data.show_demo === true)).catch(() => setShowDemo(false));
  }, []);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome to the workshop");
      nav("/");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden border-r border-border">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1636761358783-209512dccd98?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-black/90" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-2xl uppercase tracking-tight leading-none">SynqMoto<span className="text-primary"> ERP</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Two-Wheeler Ops Center</div>
            </div>
          </div>

          <div>
            <div className="text-primary text-xs uppercase tracking-[0.3em] mb-3 flex items-center gap-2">
              <Zap className="w-3 h-3" /> Command Center
            </div>
            <h1 className="font-display font-black text-6xl uppercase tracking-tighter leading-[0.9] mb-4">
              Every bolt.<br />Every rupee.<br /><span className="text-primary">Tracked.</span>
            </h1>
            <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
              Job cards, parts inventory, GST billing, and mechanic productivity — one operational hub for your workshop.
            </p>
          </div>

          <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
            v1.0 · Built for Indian Two-Wheeler Workshops
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="font-display font-black text-2xl uppercase">SynqMoto<span className="text-primary"> ERP</span></div>
          </div>

          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Sign In</div>
          <h2 className="font-display font-black text-4xl uppercase tracking-tighter mb-8">Enter the bay</h2>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
              <Input
                data-testid="login-email"
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm bg-secondary border-border font-mono text-sm h-11"
                placeholder="you@yourworkshop.com"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
              <Input
                data-testid="login-password"
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm bg-secondary border-border font-mono text-sm h-11"
                placeholder="••••••••"
                required
              />
              <div className="text-right mt-1.5">
                <Link data-testid="forgot-password-link" to="/forgot-password" className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors">Forgot password?</Link>
              </div>
            </div>
            <Button
              data-testid="login-submit"
              type="submit" disabled={busy}
              className="w-full h-11 rounded-sm bg-primary hover:bg-orange-600 text-primary-foreground uppercase tracking-[0.15em] font-bold text-sm"
            >
              {busy ? "Signing in..." : "Sign in →"}
            </Button>
          </form>

          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">New Workshop?</div>
            <a data-testid="login-register-link" href="/register" className="block border border-primary/50 hover:bg-primary/10 hover:border-primary p-3 rounded-sm text-center text-xs uppercase tracking-[0.15em] text-primary font-bold transition-colors">Register Your Workshop →</a>
          </div>

          {showDemo && (
            <div className="mt-10" data-testid="demo-accounts-panel">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Demo Accounts — tap to use</div>
              <div className="grid grid-cols-2 gap-2">
                {demoAccounts.map(a => (
                  <button
                    key={a.role}
                    data-testid={`demo-${a.role.toLowerCase()}`}
                    onClick={() => { setEmail(a.email); setPassword(a.pw); }}
                    className="text-left border border-border hover:border-primary/50 hover:bg-secondary/50 p-2 rounded-sm transition-colors"
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-primary">{a.role}</div>
                    <div className="text-xs font-mono truncate">{a.email}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
